import { useMemo, type CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  usePropertyField,
  useSlotGroup,
  useTokens,
  SectionContextProvider,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { PatternBand } from './_brandPattern';
import { getAccessibleTextOnBackground } from '../utils/luminance';

// Google Maps embed key (optional — falls back to keyless maps.google embed).
const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const selfGuidedCtaDef: SlotGroupDefinition = {
  label: 'Self-Guided Tour CTA',
  fields: {
    url: {
      label: 'Button URL',
      type: 'url',
      defaultField: 'custom_self_guided_tour_url',
      suggestedFields: ['custom_self_guided_tour_url', 'custom_rently_tour_url', 'custom_tour_url'],
      defaultValue: '#',
    },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Schedule a Self-Guided Tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const formSlot = useBlockSlot('contactform.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const selfGuidedCta = useSlotGroup('contactform.self_guided_cta', selfGuidedCtaDef);
  // Self-guided block stays hidden until a real tour URL is available.
  const selfGuidedUrl = (selfGuidedCta.url || '').trim();
  const showSelfGuided = selfGuidedUrl !== '' && selfGuidedUrl !== '#';

  const callLabel = (useContentToken('contactform.call_label') as string) || 'Call us at';
  const phone = (useContentToken('contactform.phone') as string) || '';
  const email = (useContentToken('contactform.email') as string) || '';
  const mapLabel = (useContentToken('contactform.map_label') as string) || 'Find us on the map';
  // Prefer primary on the page background; fall back when contrast is too low.
  const tokens = useTokens();
  const headingColor = useMemo(
    () => getAccessibleTextOnBackground(tokens?.color?.background, tokens?.color?.primary),
    [tokens],
  );

  // Map source — mirrors ContactMap: prefer lat/lng, then full address.
  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;
  const hasCoords = !!(lat && lng && lat.trim() !== '' && lng.trim() !== '');

  let mapSrc = '';
  if (hasCoords && MAPS_API_KEY) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=11`;
  } else if (hasCoords) {
    mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=11&output=embed`;
  } else if (address && address.trim() !== '') {
    mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&z=11&output=embed`;
  }

  return (
    <section className="cf-section">
      <div className="cf-shell">
        {/* Full-width left-aligned copy — blurbs span the shell; map stays with the form. */}
        <div className="cf-top">
          <RichTextContent
            path="contactform.heading"
            label="Heading"
            defaultValue="<p>Reach out to us</p>"
            as="h1"
            className="cf-heading"
            style={{ color: headingColor }}
          />

          <RichTextContent
            path="contactform.intro"
            label="Intro"
            defaultValue='<p>Ready to make <span data-type="field-token" data-field="property_name"></span> your new home? Apply now and take the first step toward enjoying spacious, modern apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>'
            as="div"
            className="cf-intro"
          />

          <div className="cf-lines">
            <p className="cf-line">
              <EditableToken path="contactform.call_label" as="span">
                {callLabel}
              </EditableToken>{' '}
              {phone ? (
                <a href={`tel:${phone}`} className="cf-link dniphone dniphonehref">
                  <EditableToken path="contactform.phone" as="span">
                    {phone}
                  </EditableToken>
                </a>
              ) : (
                <EditableToken path="contactform.phone" as="span">
                  {phone}
                </EditableToken>
              )}
            </p>

            {email ? (
              <a href={`mailto:${email}`} className="cf-line cf-link">
                <EditableToken path="contactform.email" as="span">
                  {email}
                </EditableToken>
              </a>
            ) : (
              <EditableToken path="contactform.email" as="p" className="cf-line">
                {email}
              </EditableToken>
            )}
          </div>

          {showSelfGuided && (
            <div className="cf-tour-block">
              <RichTextContent
                path="contactform.self_guided_heading"
                label="Self-Guided Tour Heading"
                defaultValue="<p>Now Offering Self-Guided Tours</p>"
                as="h2"
                className="cf-tour-heading"
                style={{ color: headingColor }}
              />
              <RichTextContent
                path="contactform.self_guided_blurb"
                label="Self-Guided Tour Blurb"
                defaultValue="<p>Tour on your schedule—no agent required. Book online, get an access code by email, and open the lockbox when you arrive. We&rsquo;ll check in before and after to answer questions and help with your application.</p>"
                as="div"
                className="cf-tour-blurb"
              />
              <div className="cf-cta">
                <EditableSlotGroup groupPath="contactform.self_guided_cta" definition={selfGuidedCtaDef}>
                  <a
                    href={selfGuidedCta.url || '#'}
                    target={selfGuidedCta._target || '_blank'}
                    rel={(selfGuidedCta._target || '_blank') === '_blank' ? 'noopener noreferrer' : undefined}
                    className="cf-cta-btn"
                  >
                    {selfGuidedCta.label || 'Schedule a Self-Guided Tour'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          )}

          <div className="cf-tour-block">
            <RichTextContent
              path="contactform.guided_heading"
              label="Guided Tour Heading"
              defaultValue="<p>Prefer a Guided Tour?</p>"
              as="h2"
              className="cf-tour-heading"
              style={{ color: headingColor }}
            />
            <RichTextContent
              path="contactform.body"
              label="Body Text"
              defaultValue='<p>Want a leasing agent with you? Guided tours are by appointment only—fill out the form below and we&rsquo;ll arrange your visit to <span data-type="field-token" data-field="property_name"></span>.</p>'
              as="div"
              className="cf-tour-blurb"
            />
          </div>
        </div>

        {/* Form + map only — map sits beside the form, not the blurbs. */}
        <div className="cf-form-map">
          <div
            className="cf-form-wrap"
            style={{ '--cf-heading-color': headingColor } as CSSProperties}
          >
            <EditableToken path="contactform.form_block" as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <p className="cf-form-empty">No form block selected</p>
              )}
            </EditableToken>
          </div>

          <div className="cf-map">
            <EditableToken path="contactform.map_label" as="h2" className="cf-sr-only">
              {mapLabel}
            </EditableToken>
            {mapSrc ? (
              <iframe
                className="cf-map-iframe"
                src={mapSrc}
                title={mapLabel}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="cf-map-placeholder">
                <p>Map location not available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Decorative brand-pattern band closing out the stripe, sitting flush
          above the footer. On mobile it matches the 35px band under the
          interior heroes (HeroImageBG); desktop keeps the taller closing band. */}
      <PatternBand height={240} mobileHeight={35} className="w-full bg-[var(--vibe-color-background)]" />

      <style>{`
        .cf-section {
          width: 100%;
          background: var(--vibe-color-background);
        }

        /* Wide left-aligned shell — blurbs span this width; form|map share a row below. */
        .cf-shell {
          width: 100%;
          max-width: 72rem;
          margin: 0 auto;
          padding: 2.5rem 1.75rem 3rem;
          text-align: left;
        }
        /* Tablet / smaller desktop — same side padding while form+map are stacked. */
        @media (min-width: 768px) and (max-width: 1199px) {
          .cf-shell {
            padding: 2.5rem 1.75rem 5rem;
          }
        }
        @media (min-width: 1200px) {
          .cf-shell {
            padding: 2.5rem 0.75rem 5rem;
          }
        }

        .cf-top {
          margin-bottom: 2rem;
        }

        .cf-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(2.25rem, 5vw, 3rem);
          font-weight: 600;
          line-height: 1.05;
          margin: 0 0 1.5rem;
        }
        .cf-heading p { margin: 0; }

        .cf-intro {
          font-family: var(--vibe-font-body);
          font-size: 1.125rem;
          line-height: 1.6;
          color: var(--vibe-color-text);
          max-width: none;
          margin: 0 0 1.5rem;
          text-align: left;
        }
        .cf-intro p { margin: 0; }

        .cf-tour-block {
          max-width: none;
          margin: 0 0 1.75rem;
          text-align: left;
        }
        .cf-tour-block:last-child {
          margin-bottom: 0;
        }
        .cf-tour-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.25rem, 2.5vw, 1.5rem);
          font-weight: 600;
          line-height: 1.2;
          margin: 0 0 0.75rem;
        }
        .cf-tour-heading p { margin: 0; }
        .cf-tour-blurb {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text);
          margin: 0 0 1.25rem;
        }
        .cf-tour-blurb p { margin: 0; }
        .cf-tour-blurb a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .cf-tour-block > .cf-tour-blurb:last-child {
          margin-bottom: 0;
        }

        .cf-cta {
          display: flex;
          justify-content: flex-start;
          margin: 0;
        }
        .cf-cta-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
          padding: 0.75rem 1.5rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.875rem;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          text-decoration: none;
          background: var(--bh-cta, var(--vibe-color-primary));
          color: var(--bh-cta-fg, #fff);
          border: 1.5px solid var(--bh-cta, var(--vibe-color-primary));
          border-radius: 9999px;
          transition: background-color 0.2s, border-color 0.2s, transform 0.1s, box-shadow 0.2s;
        }
        @media (min-width: 1024px) {
          .cf-cta-btn {
            padding: 1rem 3rem;
            font-size: 1rem;
          }
        }
        .cf-cta-btn:hover {
          background: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 88%, #000);
          border-color: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 30%, transparent);
        }
        .cf-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        .cf-lines {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          margin-bottom: 2rem;
        }
        .cf-line {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.4;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .cf-link {
          color: var(--vibe-color-text);
          text-decoration: none;
          transition: color 0.2s;
        }
        .cf-link:hover { color: var(--vibe-color-primary); }

        /* Form | map row — map only beside the form. */
        .cf-form-map {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          align-items: stretch;
        }
        /* Side-by-side only on larger desktop — tablet / smaller desktop stay stacked. */
        @media (min-width: 1200px) {
          .cf-form-map {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 2rem;
          }
        }

        .cf-form-wrap {
          max-width: none;
          min-width: 0;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        .cf-map {
          position: relative;
          min-height: 280px;
          min-width: 0;
        }
        @media (min-width: 1200px) {
          .cf-map {
            min-height: 100%;
          }
        }
        .cf-map-iframe,
        .cf-map-placeholder {
          display: block;
          width: 100%;
          height: 100%;
          min-height: 280px;
          border: none;
        }
        @media (min-width: 1200px) {
          .cf-map-iframe,
          .cf-map-placeholder {
            position: absolute;
            inset: 0;
            min-height: 0;
            height: 100%;
          }
        }
        .cf-map-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--vibe-color-surface, #f5f5f5);
        }
        .cf-map-placeholder p {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        .cf-sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        /* ── Multihub form card exterior — square corners ──
           The lead-form block renders its outer card (.mh-lead-form-card) with
           a rounded radius set inline; override to sharp corners so it matches
           the squared cards used elsewhere on the site. Inline styles are not
           !important, so a stylesheet !important wins. */
        .cf-form-wrap .mh-lead-form-card {
          border-radius: 0 !important;
        }

        /* Form title — match .cf-tour-heading (block sets size/color inline). */
        .cf-form-wrap .mh-lead-form-card > h3 {
          font-family: var(--vibe-font-heading) !important;
          font-size: clamp(1.25rem, 2.5vw, 1.5rem) !important;
          font-weight: 600 !important;
          line-height: 1.2 !important;
          color: var(--cf-heading-color) !important;
        }

        /* ── CTA submit button — match the site's filled pill ──
           The lead-form block renders its submit button with an 8px radius set
           inline; override to a fully-rounded pill with the same hover lift as
           the header's .nav-cta-btn so it reads as a site CTA. Not tablet-only.
           Force heading font so it matches Header › contact_link (the block
           defaults the submit label to --vibe-font-body). */
        .cf-form-wrap .mh-lead-form-submit-button {
          border-radius: 9999px !important;
          font-family: var(--vibe-font-heading) !important;
          transition: background-color 0.2s, transform 0.1s, box-shadow 0.2s !important;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled) {
          background-color: color-mix(in srgb, var(--vibe-color-primary), black 12%) !important;
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .cf-form-wrap .mh-lead-form-submit-button:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: none;
        }

        /* Half-width form column — shrink field text so two-up rows fit. */
        @media (min-width: 1200px) and (max-width: 1399px) {
          .cf-form-wrap .mh-lead-form-card input,
          .cf-form-wrap .mh-lead-form-card select,
          .cf-form-wrap .mh-lead-form-card textarea,
          .cf-form-wrap .mh-lead-form-card .mh-lead-form-date-field span {
            font-size: 0.8125rem !important;
          }
        }
        @media (max-width: 767px) {
          .cf-form-wrap .mh-lead-form-card input,
          .cf-form-wrap .mh-lead-form-card select,
          .cf-form-wrap .mh-lead-form-card textarea,
          .cf-form-wrap .mh-lead-form-card .mh-lead-form-date-field span {
            font-size: 0.75rem !important;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Reach out to us</p>',
    },
    'contactform.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        '<p>Ready to make <span data-type="field-token" data-field="property_name"></span> your new home? Apply now and take the first step toward enjoying spacious, modern apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>',
    },
    'contactform.self_guided_heading': {
      label: 'Self-Guided Tour Heading',
      type: 'richtext',
      defaultValue: '<p>Now Offering Self-Guided Tours</p>',
    },
    'contactform.self_guided_blurb': {
      label: 'Self-Guided Tour Blurb',
      type: 'richtext',
      defaultValue:
        '<p>Tour on your schedule—no agent required. Book online, get an access code by email, and open the lockbox when you arrive. We&rsquo;ll check in before and after to answer questions and help with your application.</p>',
    },
    'contactform.guided_heading': {
      label: 'Guided Tour Heading',
      type: 'richtext',
      defaultValue: '<p>Prefer a Guided Tour?</p>',
    },
    'contactform.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Want a leasing agent with you? Guided tours are by appointment only—fill out the form below and we&rsquo;ll arrange your visit to <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
    'contactform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call us at',
    },
    'contactform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'contactform.email': {
      label: 'Email',
      type: 'email',
      defaultField: 'property_email',
      defaultValue: '',
    },
    'contactform.map_label': {
      label: 'Map Label (screen-reader)',
      type: 'text',
      defaultValue: 'Find us on the map',
    },
  },
  slotGroups: {
    'contactform.self_guided_cta': selfGuidedCtaDef,
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
